import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getRouteNextSteps, type RouteNextStepsConfig } from '@/content/route-next-steps';
import { cn } from '@/lib/utils';
import { routes } from '@/routes';

export type RouteMetadata = {
  canonicalPath: string;
  title?: string;
  description?: string;
  category?: string;
};

export type RouteTitleResolver = (path: string) => string | undefined;

export type RouteNextStepsProps = {
  canonicalPath?: string;
  config?: RouteNextStepsConfig | null;
  metadata?: RouteMetadata;
  routeTitle?: string;
  resolveRouteTitle?: RouteTitleResolver;
  className?: string;
};

const routeTitleByPath = new Map(routes.map((route) => [normalizePath(route.path), route.title]));

function normalizePath(path: string): string {
  if (!path) {
    return '';
  }

  const trimmed = path.trim();
  if (!trimmed) {
    return '';
  }

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const withoutQuery = withLeadingSlash.split('?')[0]?.split('#')[0] ?? withLeadingSlash;
  const collapsed = withoutQuery.replace(/\/{2,}/g, '/');

  if (collapsed.length > 1 && collapsed.endsWith('/')) {
    return collapsed.slice(0, -1);
  }

  return collapsed || '/';
}

function toDefaultCta(path: string): string {
  if (path === '/') {
    return 'Open Calculator';
  }

  const segments = normalizePath(path).split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? '';

  if (lastSegment === 'guides') {
    return 'Browse Guides';
  }

  if (lastSegment === 'scenarios') {
    return 'Browse Scenarios';
  }

  return `Open ${lastSegment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')}`;
}

function toCanonicalHref(path: string): string {
  const normalized = normalizePath(path);
  return normalized === '/' ? '/' : `${normalized}/`;
}

function defaultResolveRouteTitle(path: string): string | undefined {
  const normalized = normalizePath(path);
  const knownTitle = routeTitleByPath.get(normalized);

  if (knownTitle) {
    return knownTitle;
  }

  const slug = normalized.split('/').filter(Boolean).pop() ?? '';
  if (!slug) {
    return undefined;
  }

  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function RouteNextSteps({
  canonicalPath,
  config,
  metadata,
  routeTitle,
  resolveRouteTitle = defaultResolveRouteTitle,
  className
}: RouteNextStepsProps) {
  const normalizedCanonicalPath = canonicalPath ? normalizePath(canonicalPath) : normalizePath(metadata?.canonicalPath ?? '');
  const resolvedConfig = config ?? (normalizedCanonicalPath ? getRouteNextSteps(normalizedCanonicalPath) : null);

  if (!resolvedConfig || !resolvedConfig.links.length) {
    return null;
  }

  const currentTitle =
    routeTitle?.trim() ||
    metadata?.title?.trim() ||
    (normalizedCanonicalPath ? resolveRouteTitle(normalizedCanonicalPath) : undefined) ||
    'this page';

  const seenPaths = new Set<string>();
  const cards = resolvedConfig.links
    .map((link) => {
      const normalizedPath = normalizePath(link.path);
      const isSelfLink = Boolean(normalizedCanonicalPath) && normalizedPath === normalizedCanonicalPath;
      const isDuplicate = seenPaths.has(normalizedPath);

      if (!normalizedPath || isSelfLink || isDuplicate) {
        return null;
      }

      seenPaths.add(normalizedPath);

      const resolvedTitle = link.label?.trim() || resolveRouteTitle(normalizedPath) || toDefaultCta(normalizedPath);
      const cta = link.cta?.trim() || toDefaultCta(normalizedPath);

      return {
        path: normalizedPath,
        reason: link.reason.trim(),
        title: resolvedTitle,
        cta
      };
    })
    .filter((card): card is { path: string; reason: string; title: string; cta: string } => Boolean(card));

  if (!cards.length) {
    return null;
  }

  const title = resolvedConfig.title?.trim() || 'Next Steps';

  return (
    <section className={cn('grid gap-4', className)}>
      <Card className="brand-frame border-border/80 bg-card/92">
        <CardHeader>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Next steps</p>
          <CardTitle className="font-display text-2xl leading-tight text-foreground">{title}</CardTitle>
          <CardDescription>{resolvedConfig.intro}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentTitle ? (
            <p className="text-sm text-muted-foreground">
              After {currentTitle}, use these pages to keep moving from decision to decision.
            </p>
          ) : null}

          <div className={cn('grid gap-3', cards.length > 1 ? 'md:grid-cols-2' : '')}>
            {cards.map((card) => (
              <Card key={card.path} className="border-border/70 bg-background/75 shadow-none">
                <CardHeader className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <CardTitle className="text-base leading-tight">{card.title}</CardTitle>
                      <CardDescription className="text-sm">{card.reason}</CardDescription>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <Button asChild variant="outline" size="sm" className="w-full justify-between">
                    <Link to={toCanonicalHref(card.path)}>
                      <span>{card.cta}</span>
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
